import {Tooltip} from '@material-ui/core';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {HOUR, SECOND} from '@wandb/weave/common/util/time';
import {Button, Checkbox as WeaveCheckbox} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Checkbox,
  Container,
  Divider,
  Header,
  Segment,
  Table,
} from 'semantic-ui-react';

import Alerts from '../../components/Alerts';
import * as AlertS from '../../components/Alerts.styles';
import {
  cloudProviderValuesToDisplayNames,
  getAzureBucketInfo,
} from '../../components/CreateTeam/hooks';
import NoMatch from '../../components/NoMatch';
import {SlackIntegrationsTable} from '../../components/SlackIntegration/SlackIntegrationsTable';
import StoragePercentage from '../../components/StoragePercentage';
import * as UserSettingsS from '../../components/UserSettings/UserSettingsProfile.styles';
import config, {envIsLocal, envIsPublicCloud} from '../../config';
import {
  Entity,
  EntitySettings,
  Organization,
  OrgType,
  PlanType,
  useEntityQuery,
  useEntityStorageBytesQuery,
  useOrganizationFlagsQuery,
  useTeamOrganizationQuery,
  useUpdateEntityMutation,
} from '../../generated/graphql';
import {convertOrganizationToOrg} from '../../pages/Billing/AccountSettings/BillingTab/PlanCard/legacy/util';
import {TeamsPlanCheckoutModal} from '../../pages/Billing/Checkout/TeamsPlanCheckoutModal';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewerMember} from '../../pages/Team/utils';
import {Analytics} from '../../services/analytics';
import {useViewer} from '../../state/viewer/hooks';
import {
  isCommunityEditionPrimarySubscription,
  isTeamsPrimarySubscription,
  PremiumFeatureTooltip,
  StripeElementsComp,
} from '../../util/accounts/pricing';
import {
  useAdminModeActive,
  viewerIsWbAdminUsingPrivileges,
  viewerUsingAdminPrivileges,
} from '../../util/admin';
import {NUM_SECONDS_IN_DAY} from '../../util/date';
import {default as DOC_URLS, default as DocUrls} from '../../util/doc_urls';
import {useEntitySettings} from '../../util/entitySettings';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import {jsonParseSafe} from '../../util/json';
import {AccessOptions} from '../../util/permissions';
import {accountSettingsTab, storageFilePath} from '../../util/urls';
import {useUserEmails} from '../../util/useUserEmails';
import AccessSettingOptions from '../AccessSettingOptions';
import TeamWBAdmin from '../Admin/TeamWBAdmin';
import {
  ArtifactTTL,
  isInvalidTTLString,
  ttlInputErrorMsg,
} from '../ArtifactTTL/utils';
import {organizationComputeSecondUsageQuery} from '../GlobalBanners/TeamsPlanEnforcementBanners/utils';
import {TeamMemberRole} from '../MembersTable/TeamMembersTable/utils';
import {TeamSettingsSecrets} from '../TeamSettings/TeamSettingsSecrets';
import {WebhooksTable} from '../Webhooks/WebhooksTable';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {LoaderProxy} from './../../containers/AppLoader';
import {DeleteTeamModal} from './DeleteTeamModal';
import {SetEntityArtifactTTLModal} from './SetEntityArtifactTTLModal';
import * as S from './TeamSettings.styles';

type Props = {
  entityName: string;
  isTeam?: boolean;
};

const ttlPolicy = {
  adminOnly: {label: 'Team admins', value: 'adminOnly'},
  allUsers: {label: 'Team admins and members', value: 'allUsers'},
};

const SET_AT_ORG_LEVEL_MESSAGE =
  "This setting is locked because it's enforced org-wide. To make changes, please contact your org admin.";

const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

export const TeamSettings = React.memo(({entityName, isTeam}: Props) => {
  const viewer = useViewer();
  const {emails} = useUserEmails();

  const {data: teamQueryData, refetch: teamQueryRefetch} = useEntityQuery({
    fetchPolicy: 'cache-and-network',
    variables: {name: entityName},
  });

  const {data: entityStorageBytesData, loading: entityStorageBytesLoading} =
    useEntityStorageBytesQuery({
      fetchPolicy: 'cache-and-network',
      variables: {name: entityName},
      skip: envIsPublicCloud,
    });

  const entity = teamQueryData?.entity;
  const entityStorageBytes = entityStorageBytesData?.entity?.storageBytes;
  const isEntityWandb = entity?.name === 'wandb';

  const matchingPrimaryEmail = emails?.find(
    e => e.emailAddress === viewer?.email
  );
  const viewerEmailIsCorporate = matchingPrimaryEmail?.type === 'CORPORATE';

  const teamOrganizationQuery = useTeamOrganizationQuery({
    variables: {entityName},
  });

  const {entitySettings, refetch: refetchEntitySettings} = useEntitySettings(
    entityName ?? ``
  );

  const [updateEntity] = useUpdateEntityMutation({
    context: propagateErrorsContext(),
  });

  const isEntityProfileEnabled =
    entitySettings?.[`entityProfileEnabled`] ?? false;

  const hideActivityCalendar =
    entitySettings?.[`hideActivityCalendar`] ?? false;

  const disableMembersEditArtifactTTL =
    entitySettings?.[`disableMembersEditArtifactTTL`] ?? false;

  const [adminOnlyEditTTLDefault, setAdminOnlyEditTTLDefault] = useState(
    disableMembersEditArtifactTTL
  );
  const [adminOnlyEditTTL, setAdminOnlyEditTTL] = useState(
    disableMembersEditArtifactTTL
  );

  const ttlDurationResult = entity?.artifactTTLDurationSeconds;
  const ttlDurationDaysString =
    ttlDurationResult > 0
      ? String(Math.round(ttlDurationResult / NUM_SECONDS_IN_DAY))
      : '';
  const [ttlDurationInputStringDefault, setTTLDurationInputStringDefault] =
    useState(ttlDurationDaysString);
  if (ttlDurationInputStringDefault !== ttlDurationDaysString) {
    setTTLDurationInputStringDefault(ttlDurationDaysString);
  }
  const [ttlDurationInputString, setTTLDurationInputString] = useState(
    ttlDurationDaysString
  );

  const hasEntityTTL =
    entity?.artifactTTLDurationSeconds !== ArtifactTTL.DISABLED;
  const [selectTeamTTLDuration, setSelectTeamTTLDuration] =
    useState(hasEntityTTL);
  const [isInvalidTTLInput, setIsInvalidTTLInput] = useState(false);

  // update entity ttl variables upon refetching entity settings
  useEffect(() => {
    setAdminOnlyEditTTLDefault(disableMembersEditArtifactTTL);
    setAdminOnlyEditTTL(disableMembersEditArtifactTTL);
    setTTLDurationInputStringDefault(ttlDurationDaysString);
    setTTLDurationInputString(ttlDurationDaysString);
    setSelectTeamTTLDuration(hasEntityTTL);
    setIsInvalidTTLInput(false);
  }, [
    teamQueryData,
    entitySettings,
    disableMembersEditArtifactTTL,
    hasEntityTTL,
    ttlDurationDaysString,
  ]);

  const updateEntitySettings = async (settings: Partial<EntitySettings>) => {
    if (!entityName) {
      return;
    }
    try {
      await updateEntity({
        variables: {
          entityName,
          settings,
        },
      });
      await refetchEntitySettings();
    } catch (e) {
      const errMsg = extractErrorMessageFromApolloError(e) ?? `${e}`;

      if (settings?.disableMagicLinkSharing) {
        toast.error(
          'An error occurred. Your team’s public sharing settings have not been updated. Try again, or contact support.'
        );
      } else {
        toast.error(
          `Error updating entity settings. Please contact support if the problem persists. Error message: ${errMsg}`
        );
      }
      console.error(errMsg);
    }
  };

  const updateEntityFields = async (fields: Partial<Entity>) => {
    if (!entityName) {
      return;
    }
    try {
      await updateEntity({
        variables: {
          entityName,
          ...fields,
        },
      });
      await teamQueryRefetch();
    } catch (e) {
      const errMsg = extractErrorMessageFromApolloError(e) ?? `${e}`;
      toast.error(
        `Error updating entity settings. Please contact support if the problem persists. Error message: ${errMsg}`
      );
      console.error(errMsg);
    }
  };

  const computeHoursRemaining =
    teamOrganizationQuery.data?.entity?.organization?.subscriptions?.find(
      s => typeof s.privileges.compute_hours === 'number'
    )?.privileges?.compute_hours;

  // TODO: remove this once server teams are linked to orgs
  let orgName;
  if (isOnPrem) {
    orgName = DEFAULT_ONPREM_ORGANIZATION_NAME;
  } else {
    orgName = teamOrganizationQuery.data?.entity?.organization?.name || '';
  }
  const organizationFlagsQuery = useOrganizationFlagsQuery({
    variables: {orgName},
  });
  const parsedOrgFlags =
    organizationFlagsQuery.data?.organization?.flags != null
      ? jsonParseSafe(organizationFlagsQuery.data?.organization?.flags)
      : {};
  const orgHasVerifiedEmailDomain =
    parsedOrgFlags.email_domain != null && parsedOrgFlags.email_domain !== '';

  const viewerIsOrgAdmin = useMemo(() => {
    if (teamOrganizationQuery.data?.entity?.organization?.members == null) {
      return false;
    }
    return (
      teamOrganizationQuery.data?.entity?.organization?.members.find(
        mem => mem.username != null && mem.username === viewer?.username
      )?.role === 'admin'
    );
  }, [
    teamOrganizationQuery.data?.entity?.organization?.members,
    viewer?.username,
  ]);

  const organizationID =
    teamOrganizationQuery.data?.entity?.organization?.id ?? '';

  const organizationName =
    teamOrganizationQuery.data?.entity?.organization?.name;

  const isAdminModeActive = useAdminModeActive();

  const orgTypeIsOrganization = useMemo(() => {
    return (
      teamOrganizationQuery.data?.entity?.organization?.orgType ===
      OrgType.Organization
    );
  }, [teamOrganizationQuery]);

  const primarySub =
    teamOrganizationQuery.data?.entity?.organization?.subscriptions?.find(
      s => s.plan.planType === PlanType.Primary
    );

  const isPrimarySubTeamsPlans =
    primarySub && isTeamsPrimarySubscription(primarySub);

  const isPrimarySubCommunityEdition =
    primarySub && isCommunityEditionPrimarySubscription(primarySub);

  const org = convertOrganizationToOrg(
    teamOrganizationQuery.data?.entity?.organization as Organization
  );

  enum ActiveModalType {
    UpgradeTeamsPlanModal = 'upgrade to teams plan modal',
    HideTeamFromNonTeamMembersConfirmationModal = 'hide this team from all non-team members confirmation modal',
  }

  const [activeModal, setActiveModal] = useState<ActiveModalType | null>(null);

  const primarySubBillingDate = primarySub?.billingPeriodStart;

  const [teamsPlanTrackedHoursInSecs, setTeamsPlanTrackedHoursInSecs] =
    useState<number | null>(null);
  useEffect(() => {
    const fetchOrgUsageIfTeamsPlan = async () => {
      if (
        primarySubBillingDate == null ||
        organizationID == null ||
        organizationName == null ||
        !isPrimarySubTeamsPlans ||
        envIsPublicCloud
      ) {
        // Conditions not met. Either don't have necessary info or primary sub not Teams
        return;
      }

      const trackedHours = await organizationComputeSecondUsageQuery(
        organizationID,
        organizationName,
        new Date(primarySubBillingDate)
      );
      setTeamsPlanTrackedHoursInSecs(trackedHours);
    };
    fetchOrgUsageIfTeamsPlan();
  }, [
    organizationID,
    organizationName,
    primarySubBillingDate,
    isPrimarySubTeamsPlans,
  ]);

  const computeHours = teamsPlanTrackedHoursInSecs ?? entity?.computeHours ?? 0;

  const viewerMember = useViewerMember(entity, viewer);

  if (entity == null) {
    return <LoaderProxy name="team-query-loader" />;
  }

  if (entity == null) {
    return <NoMatch />;
  }

  const azureBucketInfo = getAzureBucketInfo(
    entity.settings?.storageBucketInfo
  );

  const viewerCanReadTeamSettings =
    viewerMember != null || isAdminModeActive || viewerIsOrgAdmin;
  const viewerCanEditTeamSettings =
    viewerMember?.role === TeamMemberRole.ADMIN ||
    (isAdminModeActive && !viewer?.instanceAdminTeamAccessDisabled);

  if (!viewerCanReadTeamSettings) {
    return <NoMatch />;
  }

  let label = null;
  if (parsedOrgFlags.email_domain) {
    // "" might be set in Admin dashboard to unclaim a domain, so just check if it's truthy
    label = `Automatically recommend new users with matching email domains (${parsedOrgFlags.email_domain}) join this team upon signup.`;
  }
  if (!parsedOrgFlags.email_domain) {
    label = (
      <label>
        Recommend new users with matching email domains join this team upon
        signup.{' '}
        <Link
          className="font-semibold text-teal-550"
          to={accountSettingsTab(orgName, 'settings')}>
          Enable domain capture
        </Link>{' '}
        in organization settings now.
      </label>
    );
  }

  // TODO - move subcomponents into folder, use context/provider
  return (
    <div className="settings-page">
      <Container>
        <Header as="h1">Team Settings for {entity.name}</Header>
        <p>Only admins can update team settings</p>
        {entity.settings.storageBucketInfo && (
          <Segment>
            <S.SectionHeader smallMargin>External Storage</S.SectionHeader>
            <S.SectionSubheader smallMargin>
              External storage settings are displayed only for reference and
              cannot be changed.
            </S.SectionSubheader>

            <Table data-test="storage-bucket-info" basic="very">
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Cloud Provider</Table.Cell>
                  <Table.Cell data-test="storage-bucket-info-provider">
                    {
                      cloudProviderValuesToDisplayNames[
                        entity.settings.storageBucketInfo.provider
                      ]
                    }
                  </Table.Cell>
                </Table.Row>
                {
                  /* this will be truthy if the provider is Azure */
                  azureBucketInfo ? (
                    <>
                      <Table.Row>
                        <Table.Cell>Account name</Table.Cell>
                        <Table.Cell data-test="storage-bucket-info-account-name">
                          {azureBucketInfo.accountName}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Container name</Table.Cell>
                        <Table.Cell data-test="storage-bucket-info-container-name">
                          {azureBucketInfo.containerName}
                        </Table.Cell>
                      </Table.Row>
                    </>
                  ) : (
                    <Table.Row>
                      <Table.Cell>Bucket name</Table.Cell>
                      <Table.Cell data-test="storage-bucket-info-name">
                        {entity.settings.storageBucketInfo.name}
                      </Table.Cell>
                    </Table.Row>
                  )
                }

                <Table.Row>
                  <Table.Cell>Path</Table.Cell>
                  <Table.Cell data-test="storage-bucket-info-path">
                    {entity.settings.storageBucketInfo.path}
                  </Table.Cell>
                </Table.Row>

                {entity.settings.storageBucketInfo.kmsKeyID && (
                  <Table.Row>
                    <Table.Cell>KMS key ID</Table.Cell>
                    <Table.Cell data-test="storage-bucket-info-kms-key-id">
                      {entity.settings.storageBucketInfo.kmsKeyID}
                    </Table.Cell>
                  </Table.Row>
                )}

                {entity.settings.storageBucketInfo.awsExternalID && (
                  <Table.Row>
                    <Table.Cell>AWS external ID</Table.Cell>
                    <Table.Cell data-test="storage-bucket-info-aws-external-id">
                      {entity.settings.storageBucketInfo.awsExternalID}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        )}
        <Segment>
          <S.SectionHeader>Team profile</S.SectionHeader>
          {entitySettings != null && (
            <>
              <UserSettingsS.Row>
                <UserSettingsS.Label>Customization</UserSettingsS.Label>
                <UserSettingsS.Description>
                  Customize your user team page to showcase your ML portfolio
                </UserSettingsS.Description>
                <UserSettingsS.Tooltip
                  inverted
                  position="top center"
                  popperModifiers={{
                    preventOverflow: {
                      // prevent popper from erroneously constraining the popup to the
                      // table header
                      boundariesElement: 'viewport',
                    },
                  }}
                  trigger={
                    <UserSettingsS.ToggleContainer>
                      <AlertS.Toggle
                        disabled={!viewerCanEditTeamSettings}
                        toggle
                        checked={isEntityProfileEnabled}
                        onChange={() =>
                          updateEntitySettings({
                            entityProfileEnabled: !isEntityProfileEnabled,
                          })
                        }
                      />
                    </UserSettingsS.ToggleContainer>
                  }>
                  Disable for the classic profile experience
                </UserSettingsS.Tooltip>
              </UserSettingsS.Row>
              {!isTeam && (
                <UserSettingsS.Row>
                  <UserSettingsS.Label>Customization</UserSettingsS.Label>
                  <UserSettingsS.Description>
                    Hide your activity calendar on your user team page
                  </UserSettingsS.Description>
                  <UserSettingsS.Tooltip
                    inverted
                    position="top center"
                    popperModifiers={{
                      preventOverflow: {
                        // prevent popper from erroneously constraining the popup to the
                        // table header
                        boundariesElement: 'viewport',
                      },
                    }}
                    trigger={
                      <UserSettingsS.ToggleContainer>
                        <AlertS.Toggle
                          disabled={!viewerCanEditTeamSettings}
                          toggle
                          checked={hideActivityCalendar}
                          onChange={() =>
                            updateEntitySettings({
                              hideActivityCalendar: !hideActivityCalendar,
                            })
                          }
                        />
                      </UserSettingsS.ToggleContainer>
                    }>
                    Disable to hide your activity calendar on user team page
                  </UserSettingsS.Tooltip>
                </UserSettingsS.Row>
              )}
            </>
          )}
        </Segment>

        <Segment>
          <S.AlignedSection>
            <S.SectionHeader smallMargin>
              Artifacts time-to-live (TTL)
            </S.SectionHeader>
            <S.SectionHeaderPill smallMargin>
              <PremiumFeatureTooltip
                isPremiumFeatureDisabled={isPrimarySubCommunityEdition ?? false}
              />
            </S.SectionHeaderPill>
          </S.AlignedSection>
          <Tailwind style={{width: '100%'}}>
            <S.SectionSubheader smallMargin>
              Manage artifact TTL for your team. This setting ensures that
              artifact versions residing within your team will be automatically
              removed once their defined time duration comes to a close.{' '}
              <TargetBlank
                className="font-semibold text-teal-550"
                href={DOC_URLS.artifactTTL}>
                Learn more
              </TargetBlank>
            </S.SectionSubheader>
            {!isPrimarySubCommunityEdition ? (
              <Tailwind style={{width: '100%'}}>
                <div className="py-24">
                  <div className="flex h-32 items-center font-semibold">
                    TTL permissions
                    <div className="px-4 align-middle">
                      <Tooltip
                        placement="top"
                        title="Customize who can set/modify Time-to-Live (TTL) for individual artifacts.">
                        <span>
                          <Icon name="info" />
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="flex items-center py-2">
                    <Select
                      options={[ttlPolicy.adminOnly, ttlPolicy.allUsers]}
                      defaultValue={
                        disableMembersEditArtifactTTL
                          ? ttlPolicy.adminOnly
                          : ttlPolicy.allUsers
                      }
                      onChange={selected => {
                        setAdminOnlyEditTTL(
                          selected && selected.value === 'adminOnly'
                            ? true
                            : false
                        );
                      }}
                      isDisabled={!viewerCanEditTeamSettings}
                      className="w-[240px]"
                    />
                    <div className="flex px-8">
                      can &nbsp;<div className="font-semibold">set/edit</div>
                      &nbsp;TTL values for individual artifacts
                    </div>
                  </div>
                </div>

                <div className="h-32 font-semibold">Default TTL</div>
                <div className="flex h-32">
                  <div className="flex h-[24px] w-[24px] justify-center">
                    <WeaveCheckbox
                      disabled={!viewerCanEditTeamSettings}
                      checked={selectTeamTTLDuration}
                      onCheckedChange={() => {
                        setSelectTeamTTLDuration(prev => !prev);
                      }}
                    />
                  </div>
                  <div className="grid items-start px-8">
                    Set team's default TTL policy
                  </div>
                </div>

                {selectTeamTTLDuration ? (
                  <div className="h-174 w-[740px] rounded bg-moon-100 p-16">
                    <div className="mb-10 h-80">
                      <div className="h-32 font-semibold">Duration</div>
                      <div className="flex">
                        <div className="w-[70px] shrink-0">
                          <TextField
                            value={ttlDurationInputString ?? ''}
                            onChange={value => {
                              const isNumber = /^\d+$/.test(value);
                              if (!value || isNumber) {
                                setTTLDurationInputString(value ? value : '');
                                setIsInvalidTTLInput(
                                  isInvalidTTLString(value) && value !== ''
                                );
                              }
                            }}
                            errorState={isInvalidTTLInput}
                          />
                        </div>
                        <div className="flex items-center px-8 leading-none">
                          days
                        </div>
                      </div>
                      {isInvalidTTLInput && (
                        <div className="text-sm text-red-550">
                          {ttlInputErrorMsg}
                        </div>
                      )}
                    </div>

                    <div className="text-moon-600">
                      <div className="inline">
                        <li>
                          TTL team’s default applies to all existing and future
                          artifacts based on their respective creation dates.
                        </li>
                      </div>
                      <div className="inline">
                        <li>
                          Artifacts with existing version-level TTL will not be
                          affected by the team's default TTL.
                        </li>
                      </div>
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                <SetEntityArtifactTTLModal
                  entityName={entityName}
                  ttlDurationStringDefault={ttlDurationInputStringDefault}
                  ttlDurationString={ttlDurationInputString}
                  adminOnlyEditTTLDefault={adminOnlyEditTTLDefault}
                  adminOnlyEditTTL={adminOnlyEditTTL}
                  selectTeamTTLDuration={selectTeamTTLDuration}
                  entitySettings={entitySettings}
                  isInvalidTTLInput={isInvalidTTLInput}
                  saveSettings={async () => {
                    await teamQueryRefetch();
                    await refetchEntitySettings();
                  }}
                />
              </Tailwind>
            ) : (
              <div className="flex items-center justify-center">
                {viewerIsOrgAdmin ? (
                  <S.UpgradeModalLink
                    onClick={() =>
                      setActiveModal(ActiveModalType.UpgradeTeamsPlanModal)
                    }>
                    Upgrade
                  </S.UpgradeModalLink>
                ) : (
                  <span>
                    Contact an organization admin to upgrade your plan
                  </span>
                )}
                <span>&nbsp;to access this feature.</span>
              </div>
            )}
          </Tailwind>
        </Segment>

        {viewerCanEditTeamSettings && (
          <Segment>
            <S.AlignedSection>
              <S.SectionHeader smallMargin>Team alerts</S.SectionHeader>
              <S.SectionHeaderPill smallMargin>
                <PremiumFeatureTooltip
                  isPremiumFeatureDisabled={
                    isPrimarySubCommunityEdition ?? false
                  }
                />
              </S.SectionHeaderPill>
            </S.AlignedSection>

            <div>
              <S.SectionSubheader smallMargin>
                Notify the team when a member's run finishes or crashes.
              </S.SectionSubheader>

              {!isPrimarySubCommunityEdition ? (
                <div>
                  <Alerts
                    entity={entity}
                    entityRefetch={teamQueryRefetch}
                    subscriptionTypes={['SlackChannelSubscription']}
                  />

                  <S.AlignedSection>
                    <S.SectionHeader smallMargin>
                      Slack channel integrations
                    </S.SectionHeader>
                    <S.SectionHeaderPill smallMargin>
                      <PremiumFeatureTooltip
                        isPremiumFeatureDisabled={
                          isPrimarySubCommunityEdition ?? false
                        }
                      />
                    </S.SectionHeaderPill>
                  </S.AlignedSection>
                  <S.SectionSubheader smallMargin>
                    Create Slack channel integrations to send alerts to your
                    team. These can be used in an Automation.{' '}
                    <TargetBlank
                      className="font-semibold text-teal-550"
                      href={DOC_URLS.automation}>
                      Learn more
                    </TargetBlank>{' '}
                    about Automations.
                  </S.SectionSubheader>
                  <SlackIntegrationsTable
                    entity={entity}
                    entityRefetch={teamQueryRefetch}
                    disabled={!viewerCanEditTeamSettings}
                    integrationReason={
                      'Configure a Slack integration so we can send your team alerts.'
                    }
                  />
                </div>
              ) : (
                <Tailwind>
                  <div className="flex items-center justify-center">
                    {viewerIsOrgAdmin ? (
                      <S.UpgradeModalLink
                        onClick={() =>
                          setActiveModal(ActiveModalType.UpgradeTeamsPlanModal)
                        }>
                        Upgrade
                      </S.UpgradeModalLink>
                    ) : (
                      <span>
                        Contact an organization admin to upgrade your plan
                      </span>
                    )}
                    <span>&nbsp;to access this feature.</span>
                  </div>
                </Tailwind>
              )}
            </div>
          </Segment>
        )}
        <>
          <Segment>
            <S.SectionHeader smallMargin>Privacy</S.SectionHeader>
            <div className="privacy-wrapper">
              Default visibility for new projects in {entity.name}:
              <AccessSettingOptions
                isEntitySettingsPage={true}
                accessSettings={{
                  entity: {
                    id: entity.id,
                    name: entity.name,
                    readOnlyAdmin: true,
                    defaultAccess: entity.defaultAccess as AccessOptions,
                    privateOnly: entity.privateOnly ?? true,
                    isTeam: entity.isTeam,
                  },
                }}
                updateProjectAccess={(access: string) =>
                  updateEntityFields({
                    defaultAccess: access,
                  })
                }
              />
            </div>
            {/**
             * DO NOT TOGGLE THIS SETTING ON FOR THE WANDB ENTITY! https://wandb.ai/wandb/settings
             * We need public projects, custom charts, reports, etc to remain accessible for non-members.
             * If this setting is toggled on, then it will cause errors for users.
             */}
            {!isEntityWandb && (
              <>
                <Divider />
                <div className="privacy-wrapper">
                  <Tooltip
                    title={
                      parsedOrgFlags.is_hidden ? SET_AT_ORG_LEVEL_MESSAGE : ''
                    }>
                    <Checkbox
                      toggle
                      label="Hide this team from all non-members."
                      checked={entity.settings.hidden}
                      disabled={
                        !viewerCanEditTeamSettings || parsedOrgFlags.is_hidden
                      }
                      onChange={(_, {checked}) => {
                        if (checked) {
                          setActiveModal(
                            ActiveModalType.HideTeamFromNonTeamMembersConfirmationModal
                          );
                        } else {
                          updateEntitySettings({
                            hidden: checked,
                          });
                        }
                      }}
                    />
                  </Tooltip>

                  <Dialog.Root
                    open={
                      activeModal ===
                      ActiveModalType.HideTeamFromNonTeamMembersConfirmationModal
                    }
                    onOpenChange={() => {
                      setActiveModal(null);
                    }}>
                    <Dialog.Portal>
                      <Dialog.Overlay />
                      <Dialog.Content
                        className="w-[600px]"
                        style={{
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}>
                        <Dialog.Title>
                          <S.AlignedSection>
                            <Icon
                              name="warning-alt"
                              style={{marginRight: '10px'}}
                            />
                            Confirm Action
                          </S.AlignedSection>
                        </Dialog.Title>
                        <Dialog.Description>
                          <p>
                            Hiding this team from all non-members will also
                            block public access to any open projects and reports
                            belonging to this team. Are you sure you want to
                            continue?
                          </p>
                        </Dialog.Description>
                        <div className="mt-24 flex justify-end gap-8">
                          <Dialog.Close>
                            <Button
                              onClick={() => {
                                updateEntitySettings({hidden: true});
                                setActiveModal(null);
                              }}
                              variant="destructive"
                              size="medium">
                              Confirm
                            </Button>
                          </Dialog.Close>
                          <Dialog.Close>
                            <Button
                              onClick={() => {
                                setActiveModal(null);
                              }}
                              variant="ghost"
                              size="medium">
                              Cancel
                            </Button>
                          </Dialog.Close>
                        </div>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                </div>
                <Divider />
              </>
            )}

            <div className="privacy-wrapper">
              <Tooltip
                title={
                  parsedOrgFlags.private_projects
                    ? SET_AT_ORG_LEVEL_MESSAGE
                    : ''
                }>
                <Checkbox
                  toggle
                  label="Make all future team projects private (public sharing not allowed)."
                  checked={entity.privateOnly}
                  disabled={
                    !viewerCanEditTeamSettings ||
                    parsedOrgFlags.private_projects
                  }
                  onChange={(_, {checked}) =>
                    updateEntityFields({
                      privateOnly: checked,
                    })
                  }
                />
              </Tooltip>
            </div>
            <Divider />
            <div className="privacy-wrapper">
              <Tooltip
                title={
                  parsedOrgFlags.only_admins_can_invite
                    ? SET_AT_ORG_LEVEL_MESSAGE
                    : ''
                }>
                <Checkbox
                  toggle
                  label="Allow any team member to invite other members (not just admins)."
                  checked={entity.settings.membersCanInvite ?? false}
                  disabled={
                    !viewerCanEditTeamSettings ||
                    parsedOrgFlags.only_admins_can_invite
                  }
                  onChange={(_, {checked}) =>
                    updateEntitySettings({
                      membersCanInvite: checked,
                    })
                  }
                />
              </Tooltip>
            </div>
            <Divider />
            <div className="privacy-wrapper">
              <Tooltip
                title={
                  parsedOrgFlags.disable_magic_link_sharing
                    ? SET_AT_ORG_LEVEL_MESSAGE
                    : ''
                }>
                <Checkbox
                  toggle
                  label="Disable public sharing to outside of team for reports in private projects. This disables existing magic links."
                  checked={entity.settings.disableMagicLinkSharing ?? false}
                  disabled={
                    !viewerCanEditTeamSettings ||
                    parsedOrgFlags.disable_magic_link_sharing
                  }
                  onChange={(_, {checked}) => {
                    Analytics.track(
                      `DisableMagicLinkSettings ${
                        checked ? 'enabled' : 'disabled'
                      }`,
                      {
                        organizationName:
                          teamOrganizationQuery.data?.entity?.organization
                            ?.name,
                        entityName: entity?.name,
                      }
                    );
                    updateEntitySettings({
                      disableMagicLinkSharing: checked,
                    });
                  }}
                />
              </Tooltip>
            </div>

            {orgTypeIsOrganization &&
              (viewerIsOrgAdmin || isAdminModeActive) && (
                <>
                  <Divider />
                  <div className="privacy-wrapper">
                    <Tooltip
                      title={
                        entity.id ===
                        parsedOrgFlags.default_entity_id_for_domain_matching
                          ? "This option can't be disabled here as it's the default team. To change the default, go to organization settings."
                          : parsedOrgFlags.restrict_matching_org_email_domain_users
                          ? SET_AT_ORG_LEVEL_MESSAGE
                          : viewerEmailIsCorporate
                          ? 'Claim your email domain before enabling users to join teams based on your corporate email domain automatically.'
                          : 'Your primary email must be set to corporate to claim an email domain. Visit your settings page to add a work email.'
                      }>
                      <Checkbox
                        toggle
                        data-test="is-team-joinable-toggle"
                        label={label}
                        checked={
                          entity.settings?.openToMatchingOrgEmailDomainUsers ??
                          false
                        }
                        disabled={
                          entity.id ===
                            parsedOrgFlags.default_entity_id_for_domain_matching ||
                          (!viewerUsingAdminPrivileges() &&
                            (entity.readOnlyAdmin ||
                              !orgHasVerifiedEmailDomain)) ||
                          !viewerCanEditTeamSettings ||
                          parsedOrgFlags.restrict_matching_org_email_domain_users
                        }
                        onChange={(_, {checked}) => {
                          updateEntitySettings({
                            openToMatchingOrgEmailDomainUsers: checked,
                          });
                          if (checked) {
                            Analytics.track(
                              'Allow Org Members to Join Team Enabled',
                              {
                                organizationName:
                                  teamOrganizationQuery.data?.entity
                                    ?.organization?.name,
                                entityName: entity?.name,
                              }
                            );
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                </>
              )}
            <Divider />
            <div className="privacy-wrapper">
              <Tooltip
                title={
                  parsedOrgFlags.code_saving_disabled
                    ? SET_AT_ORG_LEVEL_MESSAGE
                    : ''
                }>
                <Checkbox
                  toggle
                  label="Enable code saving by default."
                  checked={entity.codeSavingEnabled}
                  disabled={
                    !viewerCanEditTeamSettings ||
                    parsedOrgFlags.code_saving_disabled
                  }
                  onChange={(_, {checked}) =>
                    updateEntityFields({
                      codeSavingEnabled: checked,
                    })
                  }
                />
              </Tooltip>
            </div>
          </Segment>
        </>
        {envIsLocal && (
          <Segment>
            <S.SectionHeader>Usage</S.SectionHeader>
            {entityStorageBytesLoading ? (
              <Loader name="entity-storage-bytes-loader" />
            ) : (
              <StoragePercentage
                available={config.MAX_BYTES}
                used={entityStorageBytes ?? 0}
                selected={0}
                labelOnTop={true}
              />
            )}
            {computeHoursRemaining != null && (
              <StoragePercentage
                available={computeHoursRemaining * (HOUR / SECOND)}
                used={computeHours}
                selected={0}
                computeHours={true}
                labelOnTop={true}
              />
            )}
            <Link
              to={storageFilePath(
                entity.organization?.name,
                entity.name,
                '',
                envIsPublicCloud,
                false
              )}>
              <Button style={{marginTop: 10}}>View Dashboard</Button>
            </Link>
          </Segment>
        )}
        {/* If this env var is not set then there is no secret store backend, so hide the UI. */}
        {/* The env var is named differently in local and always set in SaaS, so we use envIsPublicCloud instead of the env check */}
        {(config.ENABLE_SECRET_STORE || envIsPublicCloud) &&
          viewerCanEditTeamSettings && (
            <Segment>
              <TeamSettingsSecrets />
            </Segment>
          )}
        {viewerCanEditTeamSettings && (
          <Segment>
            <S.AlignedSection>
              <S.SectionHeader smallMargin>Webhooks</S.SectionHeader>
              <S.SectionHeaderPill smallMargin>
                <PremiumFeatureTooltip
                  isPremiumFeatureDisabled={
                    isPrimarySubCommunityEdition ?? false
                  }
                />
              </S.SectionHeaderPill>
            </S.AlignedSection>
            <S.SectionSubheader smallMargin>
              Configure webhooks to trigger requests to external applications
              based off certain actions in W&B. Create the webhook here and use
              it inside a W&B{' '}
              <TargetBlank
                href={DocUrls.automation + '#create-a-webhook-automation'}>
                Automation
              </TargetBlank>
              .
            </S.SectionSubheader>
            <WebhooksTable
              entity={entity}
              entityRefetch={teamQueryRefetch}
              isPremiumFeatureEnabled={!isPrimarySubCommunityEdition}
              orgToUpgrade={
                isPrimarySubCommunityEdition && org ? org : undefined
              }
            />
          </Segment>
        )}
        {entity.isTeam && viewerCanEditTeamSettings && (
          <Segment>
            <S.SectionHeader>Delete</S.SectionHeader>
            <p>Watch out, team deletion cannot be reversed.</p>
            <DeleteTeamModal teamName={entityName ?? ''} />
          </Segment>
        )}
        {viewerIsWbAdminUsingPrivileges(viewer) && (
          <TeamWBAdmin entity={entity} onUpdate={teamQueryRefetch} />
        )}

        {activeModal === ActiveModalType.UpgradeTeamsPlanModal &&
          org != null && (
            <StripeElementsComp>
              <TeamsPlanCheckoutModal
                org={org}
                onTransactionCompleted={() => {
                  teamOrganizationQuery.refetch();
                  toast('Success! Subscription updated to teams plan', {
                    position: 'top-right',
                    autoClose: 25000,
                  });
                  setActiveModal(null);
                }}
                onClose={() => setActiveModal(null)}
              />
            </StripeElementsComp>
          )}
      </Container>
    </div>
  );
});
